import { useInjection } from 'inversify-react';
import { useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { IOverlayLoaderStore } from '../../modules/common/OverlayLoader.store';

export const useLoader = (
  isInitiallyLoading: boolean = false
): { isLoading: boolean; withLoaderAsync: <T>(asyncFunction: () => Promise<T>, buttonSpinnersKey?: string) => Promise<T> } => {
  const loaderStore = useInjection(IOverlayLoaderStore.$);
  const [isLoading, setIsLoading] = useState<boolean>(isInitiallyLoading);
  const [loaders, setLoaders] = useState<Map<string, { cancelFunction: () => void; buttonSpinnersKey?: string }>>(new Map());

  useEffect(() => {
    return (): void => {
      clearLoaders();
    };
  }, []);

  const withLoaderAsync = async <T>(asyncFunction: () => Promise<T>, buttonSpinnersKey?: string): Promise<T> => {
    const id = v4();
    let cancelled = false;
    setLoaders(loaders.set(id, { buttonSpinnersKey, cancelFunction: () => (cancelled = true) }));

    setIsLoading(true);
    if (buttonSpinnersKey && loaderStore) {
      loaderStore.enableLoader(buttonSpinnersKey);
    }

    let result: T | undefined = undefined;

    try {
      result = await asyncFunction();
    } finally {
      if (!cancelled) {
        setIsLoading(false);
        if (buttonSpinnersKey && loaderStore) {
          loaderStore.disableLoader(buttonSpinnersKey);
        }
      }

      loaders.delete(id);
      setLoaders(loaders);
    }

    return result;
  };

  const clearLoaders = (): void => {
    loaders.forEach((data): void => {
      data.cancelFunction();
      if (data.buttonSpinnersKey && loaderStore) {
        loaderStore.disableLoader(data.buttonSpinnersKey);
      }
    });
  };

  return {
    isLoading,
    withLoaderAsync,
  };
};
